import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Sidebar } from 'primereact/sidebar';
import { Panel } from 'primereact/panel';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { AppCard, AppPage, CopyableText } from '../../components/general';
import { toLocalDateTime } from '../../utils/date-helper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWaterfallExecutionDetail } from '../../hooks/useVoxTracing';
import { getFileContent, searchWebhookEvents } from '../../services/voxTracingService';
import {
  WaterfallStepTraceViewModel,
  VoxOrderHistorySummaryViewModel,
  WebhookEventViewModel,
  RelatedWaterfallExecutionViewModel,
} from '../../models/voxTracing';

type RelatedWaterfallRow = RelatedWaterfallExecutionViewModel & { relationship: string };

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button
      icon={copied ? 'pi pi-check' : 'pi pi-copy'}
      label={copied ? 'Copied!' : 'Copy'}
      className="p-button-text p-button-sm"
      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', color: copied ? '#76b729' : '#8aabc8' }}
      onClick={handleCopy}
    />
  );
};

const JsonDisplay = ({ data }: { data: string }) => {
  let formatted: string;
  try {
    formatted = JSON.stringify(JSON.parse(data), null, 2);
  } catch {
    formatted = data;
  }
  return (
    <div className="rounded border-1 border-gray-200 overflow-hidden max-h-[48rem]" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', top: '0.4rem', right: '0.5rem', zIndex: 10 }}>
        <CopyButton text={formatted} />
      </div>
      <SyntaxHighlighter
        language="json"
        style={nord}
        customStyle={{ margin: 0, padding: '1rem', fontSize: '0.875rem', maxHeight: '48rem' }}
        showLineNumbers
        wrapLongLines
      >
        {formatted}
      </SyntaxHighlighter>
    </div>
  );
};

const WaterfallExecutionDetail = () => {
  const { waterfallGuid } = useParams<{ waterfallGuid: string }>();
  const navigate = useNavigate();

  const toast = useRef<Toast>(null);
  const queryClient = useQueryClient();
  const { detailQuery } = useWaterfallExecutionDetail(waterfallGuid);

  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEventViewModel | null>(null);
  const [webhookPanelVisible, setWebhookPanelVisible] = useState(false);

  const detail = detailQuery.data;

  const webhookEventsQuery = useQuery<WebhookEventViewModel[]>({
    queryKey: ['webhook-events', 'waterfall', detail?.waterfallExecutionRecordGuid],
    queryFn: () => searchWebhookEvents({ waterfallExecutionRecordGuid: detail!.waterfallExecutionRecordGuid, count: 500 }),
    enabled: !!detail?.waterfallExecutionRecordGuid,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const webhookEvents = webhookEventsQuery.data ?? [];

  const webhookPayloadQuery = useQuery<string>({
    queryKey: ['file-content', selectedWebhook?.requestPayloadFileId],
    queryFn: () => getFileContent(selectedWebhook!.requestPayloadFileId!),
    enabled: !!selectedWebhook?.requestPayloadFileId && webhookPanelVisible,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const handleStepClick = (e: any) => {
    const step = e.value as WaterfallStepTraceViewModel;
    if (step?.voxOrderHistory?.voxOrderHistoryGuid) {
      navigate(`/vox-tracing/order/${step.voxOrderHistory.voxOrderHistoryGuid}`);
    }
  };

  const formatDateTime = (value: string | null) => {
    return value ? toLocalDateTime(value) : '—';
  };

  const stepResultTemplate = (rowData: WaterfallStepTraceViewModel) => {
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Success: 'success',
      Pending: 'info',
      AwaitingCallback: 'info',
      Canceled: 'warning',
      Failed: 'danger',
      Timeout: 'warning',
    };
    return <Tag value={rowData.stepResult} severity={severityMap[rowData.stepResult] || 'info'} />;
  };

  const vendorTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return rowData.voxOrderHistory?.integrationVendor || '—';
  };

  const statusTemplate = (rowData: WaterfallStepTraceViewModel) => {
    if (!rowData.voxOrderHistory) return '—';
    const severityMap: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      Success: 'success',
      Pending: 'info',
      AwaitingCallback: 'info',
      Failed: 'danger',
      Error: 'danger',
      Timeout: 'warning',
    };
    return (
      <Tag
        value={rowData.voxOrderHistory.status}
        severity={severityMap[rowData.voxOrderHistory.status] || 'info'}
      />
    );
  };

  const dateStartedTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return formatDateTime(rowData.dateStarted);
  };

  const dateCompletedTemplate = (rowData: WaterfallStepTraceViewModel) => {
    return formatDateTime(rowData.dateCompleted);
  };

  const testCaseTemplate = (rowData: WaterfallStepTraceViewModel) => {
    if (!rowData.voxTestCaseId) return '—';
    return (
      <Tag
        icon="pi pi-exclamation-triangle"
        value={rowData.voxTestCaseName ?? `Test case #${rowData.voxTestCaseId}`}
        severity="danger"
      />
    );
  };

  const dataStatusTemplate = (rowData: WaterfallStepTraceViewModel) => {
    const voie = rowData.voieProviderDataStatus;
    const voa = rowData.voaProviderDataStatus;

    const hasVoieData = voie === 'IncomeAndEmployment' || voie === 'EmploymentOnly';
    const hasVoaData = voa === 'Assets' || voa === 'AssetsAndIncome';

    if (hasVoieData || hasVoaData) {
      const chips: { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }[] = [];
      if (voie === 'IncomeAndEmployment') chips.push({ label: 'VOIE', severity: 'success' });
      if (voie === 'EmploymentOnly') chips.push({ label: 'VOE', severity: 'info' });
      if (hasVoaData) chips.push({ label: 'VOA', severity: 'success' });
      return (
        <div className="flex gap-1 flex-wrap">
          {chips.map((chip, idx) => (
            <Tag key={idx} value={chip.label} severity={chip.severity} />
          ))}
        </div>
      );
    }

    if (voie === 'NoData' || voa === 'NoData') {
      return <Tag value="NoData" severity="danger" />;
    }

    return <Tag value="Unknown" severity="warning" />;
  };

  const executionStatusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Complete: 'success',
    Running: 'info',
    Error: 'danger',
    Abort: 'warning',
  };

  const relatedOrderStatusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    Success: 'success',
    Pending: 'info',
    AwaitingCallback: 'info',
    Failed: 'danger',
    Error: 'danger',
    Timeout: 'warning',
  };

  const handleRelatedOrderClick = (e: { value: VoxOrderHistorySummaryViewModel }) => {
    const order = e.value;
    if (order?.voxOrderHistoryGuid) {
      navigate(`/vox-tracing/order/${order.voxOrderHistoryGuid}`);
    }
  };

  const relatedOrderStatusTemplate = (rowData: VoxOrderHistorySummaryViewModel) => {
    return <Tag value={rowData.status} severity={relatedOrderStatusSeverity[rowData.status] || 'info'} />;
  };

  const relatedOrderDateTemplate = (rowData: VoxOrderHistorySummaryViewModel) => {
    return formatDateTime(rowData.createdDate);
  };

  const formatJson = (str: string | null): string => {
    if (!str) return '';
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  if (detailQuery.isLoading) {
    return (
      <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      </AppPage>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
        <AppCard>
          <div className="text-red-500">
            {detailQuery.isError
              ? `Error: ${detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load'}`
              : 'Waterfall execution record not found'}
          </div>
          <Button label="Back to Search" icon="pi pi-arrow-left" className="mt-3" onClick={() => navigate('/vox-tracing')} />
        </AppCard>
      </AppPage>
    );
  }

  const allRelatedOrders = detail!.relatedOrders ?? [];

  const { parentWaterfallExecution, childWaterfallExecutions } = detail!;
  const relatedWaterfalls: RelatedWaterfallRow[] = [
    ...(parentWaterfallExecution ? [{ ...parentWaterfallExecution, relationship: 'Launched From' }] : []),
    ...(childWaterfallExecutions ?? []).map((execution) => ({ ...execution, relationship: 'Launched' })),
  ];

  return (
    <AppPage title="Waterfall Execution Detail" breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'VoX', to: '/products/vox' }, { label: 'Transaction Logs', to: '/vox-tracing' }, { label: 'Waterfall Detail' }]}>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <Button
          label="Back to Search"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={() => navigate('/vox-tracing')}
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          className="p-button-outlined p-button-sm"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['waterfall-execution-detail'] });
            queryClient.invalidateQueries({ queryKey: ['webhook-events', 'waterfall'] });
          }}
          loading={detailQuery.isRefetching}
        />
      </div>

      <AppCard>
        <div className="flex flex-column gap-4">
          <div className="flex flex-row justify-content-between align-items-center">
            <div className="flex align-items-center gap-2">
              <h2 className="text-xl font-bold m-0">
                {detail!.waterfallName || `Waterfall #${detail!.waterfallId}`}
              </h2>
              {detail!.isTestMode && (
                <Tag icon="pi pi-exclamation-triangle" value="TEST MODE" severity="danger" className="text-sm" />
              )}
            </div>
            <Tag
              value={detail!.status}
              severity={executionStatusSeverity[detail!.status] || 'info'}
              className="text-sm"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <span className="text-500 text-sm">Loan Number</span>
              <CopyableText value={detail!.loanNumber} label="Loan Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Loan Identifier</span>
              <CopyableText value={detail!.loanIdentifier} label="Loan Identifier" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Lead Number</span>
              <CopyableText value={detail!.leadNumber} label="Lead Number" toast={toast} />
            </div>
            <div>
              <span className="text-500 text-sm">Date Started</span>
              <div className="font-semibold">{formatDateTime(detail!.dateStarted)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Date Completed</span>
              <div className="font-semibold">{formatDateTime(detail!.dateCompleted)}</div>
            </div>
            <div>
              <span className="text-500 text-sm">Execution GUID</span>
              <CopyableText value={detail!.waterfallExecutionRecordGuid} label="Execution GUID" toast={toast} />
            </div>
          </div>
        </div>
      </AppCard>

      <AppCard className="mt-3">
        <h3 className="text-lg font-semibold mb-3">Waterfall Steps</h3>
        <PrimeDataTable
          value={detail!.steps}
          selectionMode="single"
          onSelectionChange={handleStepClick}
          emptyMessage="No step trace data"
          className="cursor-pointer"
          rowClassName={(data: WaterfallStepTraceViewModel) =>
            data.voxOrderHistory ? '' : 'opacity-60'
          }
        >
          <Column field="stepResult" header="STEP RESULT" body={stepResultTemplate} />
          <Column header="VENDOR" body={vendorTemplate} />
          <Column header="ORDER STATUS" body={statusTemplate} />
          <Column field="reportType" header="REPORT TYPE" />
          <Column header="DATA STATUS" body={dataStatusTemplate} />
          <Column header="DATE STARTED" body={dateStartedTemplate} />
          <Column header="DATE COMPLETED" body={dateCompletedTemplate} />
          {detail!.isTestMode && <Column header="TEST CASE" body={testCaseTemplate} />}
          <Column field="statusMessage" header="MESSAGE" />
        </PrimeDataTable>
      </AppCard>

      {relatedWaterfalls.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Related Waterfalls</h3>
          <PrimeDataTable
            value={relatedWaterfalls}
            selectionMode="single"
            onSelectionChange={(e: { value: RelatedWaterfallRow }) =>
              navigate(`/vox-tracing/waterfall/${e.value.waterfallExecutionRecordGuid}`)
            }
            emptyMessage="No related waterfalls"
            className="cursor-pointer"
          >
            <Column field="relationship" header="RELATIONSHIP" />
            <Column
              field="waterfallName"
              header="WATERFALL"
              body={(rowData: RelatedWaterfallRow) => rowData.waterfallName || `Waterfall #${rowData.waterfallId}`}
              sortable
            />
            <Column
              field="status"
              header="STATUS"
              body={(rowData: RelatedWaterfallRow) => (
                <Tag value={rowData.status} severity={executionStatusSeverity[rowData.status] || 'info'} />
              )}
              sortable
            />
            <Column field="dateStarted" header="DATE STARTED" body={(rowData: RelatedWaterfallRow) => formatDateTime(rowData.dateStarted)} sortable />
            <Column field="dateCompleted" header="DATE COMPLETED" body={(rowData: RelatedWaterfallRow) => formatDateTime(rowData.dateCompleted)} sortable />
          </PrimeDataTable>
        </AppCard>
      )}

      <AppCard className="mt-3">
        <h3 className="text-lg font-semibold mb-3">Webhook Events</h3>
        {webhookEventsQuery.isLoading ? (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
          </div>
        ) : webhookEvents.length > 0 ? (
          <PrimeDataTable
            value={webhookEvents}
            selectionMode="single"
            onSelectionChange={(e: any) => {
              setSelectedWebhook(e.value as WebhookEventViewModel);
              setWebhookPanelVisible(true);
            }}
            emptyMessage="No webhook events"
            className="cursor-pointer"
            paginator
            rows={10}
          >
            <Column
              field="eventTimestamp"
              header="TIMESTAMP"
              body={(row: WebhookEventViewModel) => formatDateTime(row.eventTimestamp)}
              sortable
            />
            <Column field="eventType" header="EVENT TYPE" sortable />
            <Column
              field="httpStatus"
              header="HTTP STATUS"
              body={(row: WebhookEventViewModel) => (
                <Tag
                  value={String(row.httpStatus)}
                  severity={row.httpStatus >= 200 && row.httpStatus < 300 ? 'success' : 'danger'}
                />
              )}
              sortable
            />
            <Column field="vendor" header="VENDOR" sortable />
            <Column field="attempt" header="ATTEMPT" />
          </PrimeDataTable>
        ) : (
          <div className="text-500">No webhook events found for this execution.</div>
        )}
      </AppCard>

      {allRelatedOrders.length > 0 && (
        <AppCard className="mt-3">
          <h3 className="text-lg font-semibold mb-3">Related Orders</h3>
          <PrimeDataTable
            value={allRelatedOrders}
            selectionMode="single"
            onSelectionChange={handleRelatedOrderClick}
            emptyMessage="No related orders"
            className="cursor-pointer"
          >
            <Column field="transactionName" header="TRANSACTION" sortable />
            <Column field="integrationVendor" header="VENDOR" sortable />
            <Column field="status" header="ORDER STATUS" body={relatedOrderStatusTemplate} sortable />
            <Column header="DATA STATUS" body={(rowData: VoxOrderHistorySummaryViewModel) => {
              const voie = rowData.voieProviderDataStatus;
              const voa = rowData.voaProviderDataStatus;
              const hasVoieData = voie === 'IncomeAndEmployment' || voie === 'EmploymentOnly';
              const hasVoaData = voa === 'Assets' || voa === 'AssetsAndIncome';
              if (hasVoieData || hasVoaData) {
                const chips: { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }[] = [];
                if (voie === 'IncomeAndEmployment') chips.push({ label: 'VOIE', severity: 'success' });
                if (voie === 'EmploymentOnly') chips.push({ label: 'VOE', severity: 'info' });
                if (hasVoaData) chips.push({ label: 'VOA', severity: 'success' });
                return (
                  <div className="flex gap-1 flex-wrap">
                    {chips.map((chip, idx) => (
                      <Tag key={idx} value={chip.label} severity={chip.severity} />
                    ))}
                  </div>
                );
              }
              if (voie === 'NoData' || voa === 'NoData') {
                return <Tag value="NoData" severity="danger" />;
              }
              return <Tag value="Unknown" severity="warning" />;
            }} />
            <Column header="DATE STARTED" body={(rowData: VoxOrderHistorySummaryViewModel) => formatDateTime(rowData.createdDate)} sortable />
            <Column header="DATE COMPLETED" body={(rowData: VoxOrderHistorySummaryViewModel) => formatDateTime(rowData.modifiedDate)} sortable />
          </PrimeDataTable>
        </AppCard>
      )}

      <Sidebar
        visible={webhookPanelVisible}
        position="right"
        onHide={() => setWebhookPanelVisible(false)}
        style={{ width: '50vw' }}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-bolt text-xl" />
            <span className="font-semibold">Webhook Event Detail</span>
          </div>
        }
      >
        {selectedWebhook && (
          <div className="flex flex-column gap-4">
            <Panel header="Event Information" className="w-full">
              <div className="flex flex-column gap-3">
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Timestamp</div>
                  <div className="text-base font-mono">{formatDateTime(selectedWebhook.eventTimestamp)}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Event Type</div>
                  <div className="text-base">{selectedWebhook.eventType}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">HTTP Status</div>
                  <div className="text-base">
                    <Tag
                      value={String(selectedWebhook.httpStatus)}
                      severity={selectedWebhook.httpStatus >= 200 && selectedWebhook.httpStatus < 300 ? 'success' : 'danger'}
                    />
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Vendor</div>
                  <div className="text-base">{selectedWebhook.vendor || '\u2014'}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">Attempt</div>
                  <div className="text-base">{selectedWebhook.attempt}</div>
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-600 mb-1">URL</div>
                  <div className="text-sm font-mono bg-gray-100 p-2 rounded overflow-auto" style={{ wordBreak: 'break-all' }}>{selectedWebhook.url}</div>
                </div>
              </div>
            </Panel>

            {selectedWebhook.requestPayloadFileId && (
              <Panel
                header={
                  <div className="flex align-items-center gap-2">
                    <span>Request Payload</span>
                    <Tag value="JSON" severity="info" />
                  </div>
                }
                toggleable
                collapsed={false}
                className="w-full"
              >
                {webhookPayloadQuery.isLoading && (
                  <div className="flex align-items-center justify-content-center gap-2 py-4">
                    <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="4" />
                    <span className="text-sm text-gray-500">Loading payload...</span>
                  </div>
                )}
                {webhookPayloadQuery.isError && (
                  <div className="text-red-500 text-sm p-3">
                    <i className="pi pi-exclamation-triangle mr-2" />
                    Failed to load request payload
                  </div>
                )}
                {webhookPayloadQuery.data && (
                  <JsonDisplay data={webhookPayloadQuery.data} />
                )}
              </Panel>
            )}

            {selectedWebhook.response && (
              <Panel
                header={
                  <div className="flex align-items-center gap-2">
                    <span>Response</span>
                    <Tag value="JSON" severity="success" />
                  </div>
                }
                toggleable
                collapsed={false}
                className="w-full"
              >
                <JsonDisplay data={selectedWebhook.response} />
              </Panel>
            )}
          </div>
        )}
      </Sidebar>
    </AppPage>
  );
};

export default WaterfallExecutionDetail;
